import React from 'react';
import { MarketingPage } from '../components/marketing/MarketingPage';
import { Stack } from '../components/layout/Stack';
import { Text } from '../components/typography/Text';
import { FillAndCenter } from '../components/layout/FillAndCenter';
import { Spacer } from '../components/layout/Spacer';
import {
  EmailLink,
  Header,
  List,
  ListItem,
  Paragraph,
  SubHeader,
} from '../components/marketing/Marketing';

export default function PrivacyPolicyPage() {
  return (
    <MarketingPage>
      <FillAndCenter>
        <Stack spacing="xlarge" width="large">
          <Text
            size="xxlarge"
            weight="semibold"
            align="center"
            color="marketing"
          >
            Wolfia Inc. Privacy Policy
          </Text>
          <Spacer />
          <Text size="medium" weight="semibold" color="marketing">
            Last updated: July 22, 2024
          </Text>
          <Paragraph>
            This Privacy Policy explains how Wolfia Inc. ("Wolfia," "we," "us," or "our") collects, stores, uses, discloses and otherwise processes information about you in the course of our business. This policy applies to our websites (such as wolfia.com), the Wolfia Platform (our AI-powered answer engine for enterprises), and our marketing and sales activities (collectively, our "Services"). It also sets out important information about your privacy rights.
          </Paragraph>
          <Header>1. Personal Information We Collect</Header>
          <Paragraph>
            We collect information that alone or in combination with other information in our possession could be used to identify you ("Personal Information"). Here's a detailed breakdown of the types of Personal Information we collect:
          </Paragraph>
          <SubHeader>1.1 Personal Information You Provide</SubHeader>
          <Paragraph>
            When you create an account, use our Services, or communicate with us, we may collect:
          </Paragraph>
          <List>
            <ListItem>Your name and contact details (email address, phone number, mailing address)</ListItem>
            <ListItem>Login credentials (username and password)</ListItem>
            <ListItem>Company information (if you're using Wolfia for business purposes)</ListItem>
            <ListItem>Your role and job title</ListItem>
            <ListItem>Any other information you choose to provide</ListItem>
          </List>

          <SubHeader>1.2 Communication Information</SubHeader>
          <Paragraph>
            When you communicate with us, we collect:
          </Paragraph>
          <List>
            <ListItem>The content of your messages or requests</ListItem>
            <ListItem>Information about when and how you contacted us</ListItem>
            <ListItem>Information about your communication preferences</ListItem>
          </List>

          <SubHeader>1.3 Payment Information</SubHeader>
          <Paragraph>
            For paid services, we may collect:
          </Paragraph>
          <List>
            <ListItem>Credit card or other payment account information</ListItem>
            <ListItem>Billing address</ListItem>
            <ListItem>Other details necessary to process payments</ListItem>
          </List>
          <Paragraph>
            Note: We use trusted third-party payment processors and do not store full payment information on our servers.
          </Paragraph>

          <SubHeader>1.4 Social Information</SubHeader>
          <Paragraph>
            If you interact with our social media pages (e.g., LinkedIn, Twitter), we may collect:
          </Paragraph>
          <List>
            <ListItem>Your social media profile information</ListItem>
            <ListItem>Content of your posts and interactions</ListItem>
            <ListItem>Any other information you make available through your social media settings</ListItem>
          </List>

          <SubHeader>1.5 Technical Information</SubHeader>
          <Paragraph>
            When you use our Services, we automatically collect:
          </Paragraph>
          <List>
            <ListItem>Log data (IP address, browser type, pages visited, time spent)</ListItem>
            <ListItem>Device information (operating system, device type, unique identifiers)</ListItem>
            <ListItem>Usage data (features used, actions taken, preferences)</ListItem>
            <ListItem>Location data (general location based on IP address)</ListItem>
          </List>

          <SubHeader>1.6 Customer Support Information</SubHeader>
          <Paragraph>
            When you seek customer support, we may collect:
          </Paragraph>
          <List>
            <ListItem>Information about the issue you're experiencing</ListItem>
            <ListItem>Any documentation or screenshots you provide</ListItem>
            <ListItem>Information about your system and how you're using our Services</ListItem>
          </List>

          <Header>2. How We Use Personal Information</Header>
          <Paragraph>
            We do not sell your Personal Information. We use the collected information for the following purposes:
          </Paragraph>
          <List>
            <ListItem>
              <strong>Providing and Improving our Services:</strong> To operate, maintain, and enhance the Wolfia Platform and other Services, including developing new features and functionality.
            </ListItem>
            <ListItem>
              <strong>Customer Support:</strong> To respond to your inquiries, provide technical support, and improve our customer service.
            </ListItem>
            <ListItem>
              <strong>Communication:</strong> To send you important information about the Services, your account, and changes to our terms, conditions, and policies.
            </ListItem>
            <ListItem>
              <strong>Marketing and Promotions:</strong> To send you marketing communications, subject to your right to opt-out.
            </ListItem>
            <ListItem>
              <strong>Security and Fraud Prevention:</strong> To detect, prevent, and address technical issues, security threats, and fraudulent activities.
            </ListItem>
            <ListItem>
              <strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.
            </ListItem>
            <ListItem>
              <strong>Business Operations:</strong> For data analysis, audits, developing new products, enhancing our website, improving our Services, identifying usage trends, and determining the effectiveness of our promotional campaigns.
            </ListItem>
          </List>

          <Header>3. Disclosure of Personal Information</Header>
          <Paragraph>
            We may share your Personal Information with third parties in the following situations:
          </Paragraph>
          <List>
            <ListItem>
              <strong>Service Providers:</strong> We may share information with third-party vendors, consultants, and other service providers who need access to such information to carry out work on our behalf. These may include hosting providers, payment processors, analytics companies, and customer support tools.
            </ListItem>
            <ListItem>
              <strong>Business Transfers:</strong> If we're involved in a merger, acquisition, financing due diligence, reorganization, bankruptcy, receivership, sale of company assets, or transition of service to another provider, your information may be transferred as part of such a transaction.
            </ListItem>
            <ListItem>
              <strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).
            </ListItem>
            <ListItem>
              <strong>Protection of Rights:</strong> We may disclose information where we believe it's necessary to investigate, prevent, or take action regarding illegal activities, suspected fraud, situations involving potential threats to the safety of any person, or violations of our Terms of Service.
            </ListItem>
            <ListItem>
              <strong>Affiliates:</strong> We may share information with our affiliated companies for purposes consistent with this Privacy Policy.
            </ListItem>
            <ListItem>
              <strong>With Your Consent:</strong> We may share information with third parties when we have your consent to do so.
            </ListItem>
          </List>

          <Header>4. Your Rights and Choices</Header>
          <Paragraph>
            Depending on your location, you may have certain rights regarding your Personal Information:
          </Paragraph>
          <List>
            <ListItem>
              <strong>Access:</strong> You can request access to the Personal Information we hold about you.
            </ListItem>
            <ListItem>
              <strong>Correction:</strong> You can ask us to correct inaccurate or incomplete information.
            </ListItem>
            <ListItem>
              <strong>Deletion:</strong> You can request that we delete your Personal Information in certain circumstances.
            </ListItem>
            <ListItem>
              <strong>Restriction:</strong> You can ask us to restrict the processing of your information in certain circumstances.
            </ListItem>
            <ListItem>
              <strong>Portability:</strong> You may have the right to receive your Personal Information in a structured, commonly used, and machine-readable format.
            </ListItem>
            <ListItem>
              <strong>Objection:</strong> You can object to our processing of your Personal Information in certain circumstances.
            </ListItem>
            <ListItem>
              <strong>Withdraw Consent:</strong> Where we rely on your consent to process your Personal Information, you have the right to withdraw that consent at any time.
            </ListItem>
          </List>
          <Paragraph>
            To exercise these rights, please contact us at{' '}
            <EmailLink href="mailto:privacy@wolfia.com">
              privacy@wolfia.com
            </EmailLink>
            . We will respond to your request within the time period required by applicable law. Please note that we may need to verify your identity before processing your request.
          </Paragraph>

          <Header>5. Security</Header>
          <Paragraph>
            We implement appropriate technical and organizational measures to protect your Personal Information, including:
          </Paragraph>
          <List>
            <ListItem>Encryption of data in transit and at rest</ListItem>
            <ListItem>Regular security assessments and penetration testing</ListItem>
            <ListItem>Access controls and authentication measures</ListItem>
            <ListItem>Employee training on data protection and security</ListItem>
            <ListItem>Incident response plans</ListItem>
          </List>
          <Paragraph>
            While we strive to protect your Personal Information, no method of transmission over the Internet or method of electronic storage is 100% secure. Therefore, we cannot guarantee absolute security.
          </Paragraph>

          <Header>6. Data Retention and Deletion</Header>
          <Paragraph>
            We retain your Personal Information for as long as necessary to provide you with our Services, comply with our legal obligations, resolve disputes, and enforce our agreements. The criteria used to determine our retention periods include:
          </Paragraph>
          <List>
            <ListItem>The length of time you use our Services</ListItem>
            <ListItem>Legal obligations under applicable laws</ListItem>
            <ListItem>Statute of limitations under applicable laws</ListItem>
            <ListItem>Ongoing or potential legal disputes</ListItem>
            <ListItem>Guidelines issued by relevant data protection authorities</ListItem>
          </List>
          <Paragraph>
            When we no longer need to process your Personal Information, we will either delete or anonymize it. If this is not possible (for example, because your Personal Information has been stored in backup archives), we will securely store your Personal Information and isolate it from any further processing until deletion is possible.
          </Paragraph>

          <Header>7. Children's Privacy</Header>
          <Paragraph>
            Our Services are not directed to individuals under the age of 16. We do not knowingly collect Personal Information from children under 16. If we become aware that a child under 16 has provided us with Personal Information, we will take steps to delete such information. If you become aware that a child has provided us with Personal Information, please contact us at{' '}
            <EmailLink href="mailto:privacy@wolfia.com">
              privacy@wolfia.com
            </EmailLink>
            .
          </Paragraph>

          <Header>8. International Data Transfers</Header>
          <Paragraph>
            Your Personal Information may be transferred to, and processed in, countries other than the country in which you are resident. These countries may have data protection laws that are different from the laws of your country.
          </Paragraph>
          <Paragraph>
            Specifically, our servers are located in the United States, and our third-party service providers and partners operate around the world. This means that when we collect your Personal Information, we may process it in any of these countries.
          </Paragraph>
          <Paragraph>
            We have taken appropriate safeguards to require that your Personal Information will remain protected in accordance with this Privacy Policy. These include implementing the European Commission's Standard Contractual Clauses for transfers of Personal Information between our group companies and third-party service providers and partners, which require all such recipients to protect Personal Information that they process from the EEA in accordance with European data protection laws.
          </Paragraph>

          <Header>9. Google Workspace APIs</Header>
          <Paragraph>
            Wolfia Inc. explicitly affirms that we do not use Google Workspace APIs to develop, improve, or train generalized AI and/or ML models. Any data obtained through Google Workspace APIs is used solely for the specific purposes outlined in this Privacy Policy and in accordance with our agreement with Google and applicable Google API Services User Data Policy.
          </Paragraph>

          <Header>10. Changes to Our Privacy Policy</Header>
          <Paragraph>
            We may update this Privacy Policy from time to time in response to changing legal, technical, or business developments. When we update our Privacy Policy, we will take appropriate measures to inform you, consistent with the significance of the changes we make. We will obtain your consent to any material Privacy Policy changes if and where this is required by applicable data protection laws.
          </Paragraph>
          <Paragraph>
            You can see when this Privacy Policy was last updated by checking the "last updated" date displayed at the top of this Privacy Policy.
          </Paragraph>

          <Header>11. How to Contact Us</Header>
          <Paragraph>
            If you have any questions or concerns about our Privacy Policy or our data practices, you can contact us in the following ways:
          </Paragraph>
          <List>
            <ListItem>
              Email: {' '}
              <EmailLink href="mailto:privacy@wolfia.com">
                privacy@wolfia.com
              </EmailLink>
            </ListItem>
            <ListItem>
              Mail: Wolfia Inc., 10500 Avery Club Drive, Unit 6, Austin TX - 78717
            </ListItem>
          </List>
          <Paragraph>
            You have the right to complain to a data protection authority about our collection and use of your Personal Information. For more information, please contact your local data protection authority.
          </Paragraph>

          <Spacer />
        </Stack>
      </FillAndCenter>
    </MarketingPage>
  );
}